import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import {
  addOrganizationMember,
  deleteUser,
  DerinMembership,
  DerinOrganization,
  DerinUser,
  DerinUserRole,
  MembershipRequest,
  removeOrganizationMember,
  updateOrganizationMemberRole,
} from "../../../api/derin";
import ModalShell from "../CollectionsTab/ModalShell";
import { collectionColors, font, outlinedButtonSx, primaryButtonSx, subtitleSx } from "../CollectionsTab/tokens";
import { errorMessageOf } from "../errorMessage";
import { useToast } from "../Toast";
import OrganizationAccessRows, { emptyRow } from "./OrganizationAccessRows";
import {
  dangerNoticeSx,
  formatMemberSince,
  initialsOf,
  organizationLabel,
  ROLE_OPTIONS,
  selectSx,
} from "./styles";

interface Props {
  user: DerinUser;
  organizations: DerinOrganization[];
  onBack: () => void;
  /** Called after anything changed, so the list behind this view can refetch. */
  onChanged: () => void | Promise<void>;
}

const IRREVERSIBLE = "This action takes effect immediately and cannot be undone.";

const dangerOutlinedButtonSx = {
  ...outlinedButtonSx,
  padding: "6px 10px",
  border: `1px solid ${collectionColors.danger}`,
  color: collectionColors.danger,
  "&:hover": { background: collectionColors.dangerSurface },
};

/**
 * One user's page in the Users tab: their organizations and the role in each, edited
 * in place. Removing an organization or the whole user asks for confirmation first.
 */
const UserDetailView: React.FC<Props> = ({ user, organizations, onBack, onChanged }) => {
  const { showSuccess, showError } = useToast();
  const [memberships, setMemberships] = useState<DerinMembership[]>(user.memberships);
  const [busyOrganizationId, setBusyOrganizationId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<DerinMembership | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newRows, setNewRows] = useState<MembershipRequest[]>([emptyRow()]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const name = `${user.firstName} ${user.lastName}`;
  const memberOf = memberships.map((m) => m.organizationId);
  const canAdd = organizations.some((o) => !memberOf.includes(o.id));

  const changeRole = async (membership: DerinMembership, role: DerinUserRole) => {
    setBusyOrganizationId(membership.organizationId);
    try {
      await updateOrganizationMemberRole(membership.organizationId, user.id, role);
      setMemberships((current) =>
        current.map((m) => (m.organizationId === membership.organizationId ? { ...m, role } : m))
      );
      showSuccess(`${name} is now ${role} in ${membership.organizationName}`);
      onChanged();
    } catch (err) {
      showError(errorMessageOf(err));
    } finally {
      setBusyOrganizationId(null);
    }
  };

  const handleRemove = async () => {
    if (!removing) return;
    setIsSubmitting(true);
    try {
      await removeOrganizationMember(removing.organizationId, user.id);
      setMemberships((current) => current.filter((m) => m.organizationId !== removing.organizationId));
      showSuccess(`${name} was removed from ${removing.organizationName}`);
      setRemoving(null);
      onChanged();
    } catch (err) {
      showError(errorMessageOf(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await deleteUser(user.id);
      // Refetch before going back, or the list would still show them for a moment.
      await onChanged();
      showSuccess(`${name} was deleted`);
      onBack();
    } catch (err) {
      showError(errorMessageOf(err));
      setIsSubmitting(false);
    }
  };

  const openAdd = () => {
    setNewRows([emptyRow()]);
    setIsAddOpen(true);
  };

  const handleAdd = async () => {
    setIsSubmitting(true);
    const added: DerinMembership[] = [];
    try {
      for (const row of newRows) {
        await addOrganizationMember(row.organizationId, user.id, row.role);
        const organization = organizations.find((o) => o.id === row.organizationId);
        added.push({
          organizationId: row.organizationId,
          organizationName: organization?.name ?? row.organizationId,
          role: row.role,
          memberSince: new Date().toISOString(),
        });
      }
      showSuccess(`${name} was added to ${added.map((m) => m.organizationName).join(", ")}`);
      setIsAddOpen(false);
    } catch (err) {
      showError(errorMessageOf(err));
    } finally {
      // Keep whatever was saved before a failure.
      if (added.length > 0) {
        setMemberships((current) =>
          [...current, ...added].sort((a, b) => a.organizationName.localeCompare(b.organizationName))
        );
        onChanged();
      }
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        background: collectionColors.white,
        border: `1px solid ${collectionColors.border}`,
        borderRadius: "14px",
        mt: 4,
        maxWidth: "1100px",
        mx: "auto",
        padding: "24px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={onBack}
        sx={{
          alignSelf: "flex-start",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          border: "none",
          background: "none",
          padding: 0,
          cursor: "pointer",
          fontFamily: font,
          fontSize: "13px",
          color: collectionColors.textPrimary,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 14 }} />
        Back to all users
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          pb: "20px",
          borderBottom: `1px solid ${collectionColors.border}`,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "#E6F4F1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: font,
            fontSize: "14px",
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {initialsOf(user.firstName, user.lastName)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: font, fontWeight: 600, fontSize: "18px", color: collectionColors.textPrimary }}>
            {name}
          </Typography>
          <Typography sx={{ ...subtitleSx, fontSize: "13px" }}>{user.email}</Typography>
        </Box>
        <Button sx={dangerOutlinedButtonSx} onClick={() => setIsDeleteOpen(true)}>
          Delete user
        </Button>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px" }}>
        <Box>
          <Typography sx={{ fontFamily: font, fontWeight: 600, fontSize: "15px", color: collectionColors.textPrimary }}>
            Organization memberships
          </Typography>
          <Typography sx={{ ...subtitleSx, fontSize: "13px" }}>Edit this user's role in each organization.</Typography>
        </Box>
        <Button
          sx={primaryButtonSx}
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          onClick={openAdd}
          disabled={!canAdd}
        >
          Add organization
        </Button>
      </Box>

      <Box sx={{ border: `1px solid ${collectionColors.border}`, borderRadius: "10px", overflow: "hidden" }}>
        {memberships.length === 0 && (
          <Typography sx={{ ...subtitleSx, padding: "16px" }}>
            No organization access. This user can't sign in until added to one.
          </Typography>
        )}
        {memberships.map((membership, index) => {
          const since = formatMemberSince(membership.memberSince);
          const isBusy = busyOrganizationId === membership.organizationId;
          return (
            <Box
              key={membership.organizationId}
              data-testid={`membership-${membership.organizationId}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px 16px",
                borderTop: index === 0 ? "none" : `1px solid ${collectionColors.border}`,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  background: "#ECEEF2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ApartmentOutlinedIcon sx={{ fontSize: 18, color: collectionColors.textSecondary }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: font, fontWeight: 500, fontSize: "14px", color: collectionColors.textPrimary }}>
                  {organizationLabel(membership.organizationId, membership.organizationName, organizations)}
                </Typography>
                {since && <Typography sx={{ ...subtitleSx, fontSize: "12px" }}>Member since {since}</Typography>}
              </Box>
              <Box
                component="select"
                aria-label={`Role in ${membership.organizationName}`}
                value={membership.role}
                disabled={isBusy}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  changeRole(membership, e.target.value as DerinUserRole)
                }
                sx={{ ...selectSx, width: "110px" }}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </Box>
              <Button
                sx={{ ...dangerOutlinedButtonSx, height: "28px", fontSize: "12px" }}
                aria-label={`Remove from ${membership.organizationName}`}
                disabled={isBusy}
                onClick={() => setRemoving(membership)}
              >
                Remove
              </Button>
            </Box>
          );
        })}
      </Box>

      <ModalShell
        open={!!removing}
        onClose={() => setRemoving(null)}
        title="Remove organization access?"
        subtitle={
          removing
            ? `Remove ${name} from ${removing.organizationName}? Their user account and access to other organizations will remain active.`
            : undefined
        }
        confirmLabel="Remove access"
        onConfirm={handleRemove}
        isSubmitting={isSubmitting}
        danger
      >
        <Box sx={dangerNoticeSx}>{IRREVERSIBLE}</Box>
      </ModalShell>

      <ModalShell
        open={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete user permanently?"
        subtitle={`Delete ${name} and remove all of their organization memberships? The user will no longer be able to access any organization.`}
        confirmLabel="Delete user"
        onConfirm={handleDelete}
        isSubmitting={isSubmitting}
        danger
      >
        <Box sx={dangerNoticeSx}>{IRREVERSIBLE}</Box>
      </ModalShell>

      <ModalShell
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add organization"
        subtitle={`Give ${name} access to another organization.`}
        confirmLabel="Add to organization"
        onConfirm={handleAdd}
        confirmDisabled={!newRows.every((r) => r.organizationId)}
        isSubmitting={isSubmitting}
      >
        <OrganizationAccessRows
          rows={newRows}
          onChange={setNewRows}
          organizations={organizations}
          excludedOrganizationIds={memberOf}
          description="Select an organization this user does not already belong to."
        />
      </ModalShell>
    </Box>
  );
};

export default UserDetailView;
